import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Scrim } from './Scrim';
import { cn } from '../lib/utils';
import { useLanguage } from '../functions/useLanguage';

export const SKIP_DELETE_CONFIRM_KEY = 'koda_skip_delete_confirm';

interface DeleteConfirmModalProps {
  isOpen: boolean;
  title?: string;
  description?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * A reusable confirmation modal for destructive delete actions.
 * Includes a "Don't show again" checkbox that, when checked and confirmed,
 * persists the preference to localStorage so future deletes skip the modal.
 */
export function DeleteConfirmModal({
  isOpen,
  title,
  description,
  onConfirm,
  onCancel,
}: DeleteConfirmModalProps) {
  const { t } = useLanguage();
  const [dontShow, setDontShow] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (dontShow) {
      localStorage.setItem(SKIP_DELETE_CONFIRM_KEY, 'true');
    }
    onConfirm();
  };

  return (
    <Scrim onClick={onCancel} zIndex={300}>
      <div
        className={cn(
          'rounded-2xl w-full max-w-sm mx-4 shadow-xl animate-in fade-in zoom-in duration-200',
          'bg-white text-text-primary'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 flex flex-col items-center text-center gap-5">
          {/* Icon */}
          <div className={cn(
            'w-14 h-14 rounded-full flex items-center justify-center',
            'bg-error-red/10'
          )}>
            <Trash2 size={26} className="text-error-red" />
          </div>

          {/* Title + Description */}
          <div className="space-y-1.5">
            <h3 className={cn('text-lg font-bold', 'text-text-primary')}>
              {title || t('deleteConfirm.defaultTitle')}
            </h3>
            {description && (
              <p className={cn('text-sm leading-relaxed', 'text-text-primary')}>
                {description}
              </p>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 w-full">
            <button
              onClick={onCancel}
              className={cn(
                'flex-1 py-3 rounded-xl font-semibold text-sm transition-colors',
                'bg-gray-100 text-text-secondary hover:bg-gray-200'
              )}
            >
              {t('deleteConfirm.cancel')}
            </button>
            <button
              onClick={handleConfirm}
              className="flex-1 py-3 rounded-xl font-semibold text-sm bg-error-red text-white hover:bg-error-red/90 active:scale-[0.97] transition-all"
            >
              {t('deleteConfirm.delete')}
            </button>
          </div>

          {/* Don't show again */}
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontShow}
              onChange={(e) => setDontShow(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 accent-error-red cursor-pointer"
            />
            <span className={cn('text-xs', 'text-text-secondary')}>
              {t('deleteConfirm.dontShowAgain')}
            </span>
          </label>
        </div>
      </div>
    </Scrim>
  );
}
